import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatBRL(cents: number | string | null | undefined): string {
  if (cents === null || cents === undefined) return 'R$ 0,00';
  const n = typeof cents === 'string' ? Number(cents) : cents;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(n / 100);
}

export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '0';
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function formatPercent(value: number | string | null | undefined, decimals = 1): string {
  if (value === null || value === undefined) return '0%';
  const n = typeof value === 'string' ? Number(value) : value;
  return `${(n * 100).toFixed(decimals)}%`;
}

export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(d, { locale: ptBR, addSuffix: true });
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'dd/MM/yyyy', { locale: ptBR });
}

// ----- Cores de temperatura -----
export const temperatureColor: Record<string, string> = {
  quente: 'bg-red-500/15 text-red-400 border-red-500/30',
  morno: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  frio: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  gelado: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

export const temperatureLabel: Record<string, string> = {
  quente: '🔥 Quente',
  morno: '🌡️ Morno',
  frio: '❄️ Frio',
  gelado: '🧊 Gelado',
};

// ----- Lifecycle stages -----
export const stageOrder = [
  'suspect',
  'visitor',
  'engaged',
  'identified',
  'checkout_started',
  'payment_pending',
  'payment_failed',
  'customer_front',
  'upsell_decision',
  'customer_pro',
  'install_decision',
  'active_customer',
  'at_risk',
  'won',
  'lost',
] as const;

export const stageLabel: Record<string, string> = {
  suspect: 'Suspect',
  visitor: 'Visitante',
  engaged: 'Engajado',
  identified: 'Identificado',
  checkout_started: 'Checkout',
  payment_pending: 'Pgto. Pendente',
  payment_failed: 'Pgto. Falhou',
  customer_front: 'Front',
  upsell_decision: 'Upsell',
  customer_pro: 'Pro',
  install_decision: 'Install',
  active_customer: 'Ativo',
  at_risk: 'Em Risco',
  won: 'Ganho',
  lost: 'Perdido',
};

export const stageColor: Record<string, string> = {
  suspect: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  visitor: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  engaged: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  identified: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  checkout_started: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  payment_pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  payment_failed: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  customer_front: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  upsell_decision: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  customer_pro: 'bg-green-500/15 text-green-400 border-green-500/30',
  install_decision: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  active_customer: 'bg-green-500/20 text-green-300 border-green-500/40',
  at_risk: 'bg-red-500/15 text-red-400 border-red-500/30',
  won: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  lost: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
};

// ----- Priority -----
export const priorityLabel: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normal',
  low: 'Baixa',
};

export const priorityColor: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-300 border-red-500/40',
  high: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  normal: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  low: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

// ----- Status order/conversation/ticket -----
export const orderStatusColor: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  refunded: 'bg-orange-500/15 text-orange-400',
  chargeback: 'bg-red-500/20 text-red-300',
  cancelled: 'bg-slate-500/15 text-slate-400',
};

export function sentimentLabel(score: number | null | undefined): {
  emoji: string;
  label: string;
  color: string;
} {
  if (score === null || score === undefined) return { emoji: '😐', label: 'Neutro', color: 'text-muted-foreground' };
  const n = Number(score);
  if (n < -0.5) return { emoji: '😡', label: 'Irritado', color: 'text-red-400' };
  if (n < -0.1) return { emoji: '😟', label: 'Insatisfeito', color: 'text-orange-400' };
  if (n < 0.1) return { emoji: '😐', label: 'Neutro', color: 'text-slate-400' };
  if (n < 0.5) return { emoji: '🙂', label: 'Satisfeito', color: 'text-blue-400' };
  return { emoji: '😊', label: 'Muito satisfeito', color: 'text-emerald-400' };
}
